import { useEffect, useState } from "react";

import { IDashboardStats, UserPartsStats, UserStats } from "../../models/DashboardStats";
import DashboardStatTableTabs from "../shared/Tabs/DashboardStatTableTabs";
import DashboardTile from "./DashboardTile";
import { ClockIcon } from "@heroicons/react/24/solid";
import { useNextCronCountdown } from "../../data/useNextCronCountdown";

const tabs = [
  { name: 'Today', value: 'today' },
  { name: 'Week', value: 'week' },
  { name: 'Month', value: 'month' },
];

interface IActivityTableProps {
  dashboardStats: IDashboardStats;
  dayKey: StatKey;
  weekKey: StatKey;
  monthKey: StatKey;
  title: string;
  keyCol: string;
}

type StatKey = keyof IDashboardStats;

export default function PartsUserActivityTable({
  dashboardStats,
  dayKey,
  weekKey,
  monthKey,
  title,
  keyCol,
}: IActivityTableProps) {
  const [timeframe, setTimeframe] = useState("today");
  const [stats, setStats] = useState<{ [key: string]: UserPartsStats } | undefined>(undefined);
  const [removedStats, setRemovedStats] = useState<{ [key: string]: UserStats } | undefined>(undefined);
  const [combinedKeys, setCombinedKeys] = useState<string[]>([]);
  const countdown = useNextCronCountdown();
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    if (dashboardStats) {
      switch (timeframe) {
        case "today":
          let dayStats = dashboardStats[dayKey] as { [key: string]: UserPartsStats };
          let dayRemovedStats = dashboardStats["removedDay"] as { [key: string]: UserStats };
          setStats(dayStats);
          setRemovedStats(dayRemovedStats);
          setCombinedKeys(Array.from(new Set([...Object.keys(dayStats), ...Object.keys(dayRemovedStats)])));
          break;
        case "week":
          let weekStats = dashboardStats[weekKey] as { [key: string]: UserPartsStats };
          let weekRemovedStats = dashboardStats["removedWeek"] as { [key: string]: UserStats };
          setStats(weekStats);
          setRemovedStats(weekRemovedStats);
          setCombinedKeys(Array.from(new Set([...Object.keys(weekStats), ...Object.keys(weekRemovedStats)])));
          break;
        case "month":
          let monthStats = dashboardStats[monthKey] as { [key: string]: UserPartsStats };
          let monthRemovedStats = dashboardStats["removedMonth"] as { [key: string]: UserStats };
          setRemovedStats(monthRemovedStats);
          setStats(monthStats);
          setCombinedKeys(Array.from(new Set([...Object.keys(monthStats), ...Object.keys(monthRemovedStats)])));
          break;
      }
    }
  }, [dashboardStats, timeframe]);
  

  return (
    <DashboardTile span={2}>
      <div className="p-3">
        <div className="mb-3 w-full flex justify-between">
          <div className="prose">
            <h3>
              <div
                className="relative inline-flex items-center"
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
              >
                <ClockIcon className="inline-block w-6 h-6 mr-2 text-gray-500 hover:text-primary transition-all duration-200 hover:scale-110 cursor-pointer" /> 
                
                {title}
                
                {showTooltip && (
                  <div className="absolute left-0 top-full mt-2  whitespace-nowrap rounded-md bg-gray-800 px-2 py-1 text-xs text-white shadow-lg z-10">
                    Recalculating in {countdown}
                  </div>
                )}
              </div>             
            </h3>
          </div>

          <DashboardStatTableTabs
            tabs={tabs}
            value={timeframe}
            onChange={setTimeframe}
            ariaLabel="Select a tab"
          />

        </div>

        <div className="mt-6 overflow-x-auto">
          <table className="min-w-full border-collapse text-center text-xs">
            <thead>
              <tr className="border-b">
                <th className="text-left pb-2">{keyCol}</th>
                <th>Created</th>
                <th className="bg-primary-50">Removed</th>
                <th>Imaged</th>
                <th className="bg-primary-50">Located</th>
                <th>Priced</th>
                <th className="bg-primary-50">Listed</th>
                <th>Picked</th>
                <th className="bg-primary-50">Shipped</th>
                <th>Scrapped</th>
                <th className="bg-primary-50">Deleted</th>
              </tr>
            </thead>

            <tbody className="divide-y">
              {stats &&
                combinedKeys.map((key) => {
                  const row = stats[key];
                  const removedRow = removedStats ? removedStats[key] : null;
                  if (!row && !removedRow) return null;

                  return (
                    <tr key={key} className="font-semibold text-center">
                      <td className="text-left pt-2">{key}</td>
                      <td className="font-bold text-md text-primary-600">{row?.createdCount ?? 0}</td>
                      <td className="font-bold text-md text-primary-600 bg-primary-50">{removedRow?.removedCount ?? 0}</td>
                      <td className="font-bold text-md text-primary-600">{row?.imagedCount ?? 0}</td>
                      <td className="font-bold text-md text-primary-600 bg-primary-50">{row?.locatedCount ?? 0}</td>
                      <td className="font-bold text-md text-primary-600">{row?.pricedCount ?? 0}</td>
                      <td className="font-bold text-md text-primary-600 bg-primary-50">{row?.listedCount ?? 0}</td>
                      <td className="font-bold text-md text-primary-600">{row?.pickedCount ?? 0}</td>
                      <td className="font-bold text-md text-primary-600 bg-primary-50">{row?.shippedCount ?? 0}</td>
                      <td className="font-bold text-md text-primary-600">{row?.scrappedCount ?? 0}</td>
                      <td className="font-bold text-md text-primary-600 bg-primary-50">{row?.deletedCount ?? 0}</td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>


      </div>
    </DashboardTile>
  );

}